import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Users, ShieldCheck, Crown, DollarSign, RefreshCw, LogOut } from "lucide-react";

function StatCard({ label, value, hint, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-rule bg-white p-5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">{label}</p>
        <span className="grid place-items-center h-7 w-7 rounded-full bg-forest/10 text-forest">
          <Icon size={13} />
        </span>
      </div>
      <p className="mt-2 font-serif text-3xl text-ink">{value}</p>
      {hint && <p className="mt-1 text-[12px] text-ink-soft">{hint}</p>}
    </div>
  );
}

export default function AdminUsers() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [updating, setUpdating] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando datos admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const toggleRole = async (u) => {
    const next = u.role === "admin" ? "student" : "admin";
    if (u.user_id === user?.user_id && next !== "admin") {
      toast.warning("No puedes quitarte el rol de admin a ti mismo");
      return;
    }
    setUpdating(u.user_id);
    try {
      await api.patch(`/admin/users/${u.user_id}/role`, { role: next });
      toast.success(`Rol actualizado a ${next === "admin" ? "Admin" : "Estudiante"}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el rol");
    } finally {
      setUpdating(null);
    }
  };

  const filtered = users.filter(
    (u) =>
      !q ||
      u.email.toLowerCase().includes(q.toLowerCase()) ||
      u.name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="admin-page">
      <header className="border-b border-rule bg-paper/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Link to="/" className="inline-flex items-baseline gap-0.5">
              <span className="font-serif italic text-2xl text-copper">una</span><span className="font-serif text-2xl text-ink">TESIS</span>
            </Link>
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-forest/10 text-forest px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-medium">
              <ShieldCheck size={11} /> Panel admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/bank-transfers" data-testid="admin-link-bank-transfers" className="hidden sm:inline-flex items-center gap-1.5 text-[13px] text-ink-soft hover:text-forest">
              <span className="grid place-items-center h-6 w-6 rounded-full bg-forest/10 text-forest">$</span> Transferencias BBVA
            </Link>
            <Link to="/dashboard" className="text-[13px] text-ink-soft hover:text-forest inline-flex items-center gap-1.5">
              <ArrowLeft size={13} /> Mi escritorio
            </Link>
            <button onClick={onLogout} className="h-8 w-8 grid place-items-center rounded-full hover:bg-paper-2 text-ink-soft hover:text-ink" title="Cerrar sesión">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">Operación</p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink">Usuarios y planes</h1>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={loadAll}
            className="inline-flex items-center gap-2 text-[13px] text-ink-soft hover:text-forest"
          >
            <RefreshCw size={13} /> Refrescar
          </button>
        </div>

        {stats && (
          <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard testid="admin-stat-users" label="Usuarios totales" value={stats.total_users} hint={`${stats.total_admins} admin · ${stats.total_students} estudiantes`} icon={Users} />
            <StatCard testid="admin-stat-free" label="Plan gratis" value={stats.free_users} icon={Users} />
            <StatCard testid="admin-stat-premium" label="Plan Premium" value={stats.premium_users} icon={Crown} />
            <StatCard testid="admin-stat-revenue" label="Ingresos USD" value={`$${stats.total_revenue_usd.toFixed(2)}`} hint={`${stats.paid_transactions} pagos`} icon={DollarSign} />
          </div>
        )}

        <div className="mt-10 rounded-2xl border border-rule bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-rule flex items-center justify-between gap-4">
            <p className="font-serif text-xl text-ink">Base de datos de usuarios</p>
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o correo…"
              className="px-3 py-2 text-[13px] rounded-lg border border-rule bg-paper-2/50 focus:bg-white focus:border-forest outline-none w-64"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="admin-users-table">
              <thead className="bg-paper-2/40 text-ink-soft">
                <tr className="text-left">
                  <th className="px-5 py-3 font-medium tracking-wide">Usuario</th>
                  <th className="px-5 py-3 font-medium tracking-wide">Proveedor</th>
                  <th className="px-5 py-3 font-medium tracking-wide">Rol</th>
                  <th className="px-5 py-3 font-medium tracking-wide">Plan</th>
                  <th className="px-5 py-3 font-medium tracking-wide">Creado</th>
                  <th className="px-5 py-3 font-medium tracking-wide text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-ink-soft">Cargando…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-ink-soft">Sin resultados.</td></tr>
                )}
                {filtered.map((u) => (
                  <tr key={u.user_id} className="border-t border-rule hover:bg-paper-2/30" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-forest text-paper grid place-items-center font-serif text-xs">{u.name?.[0]?.toUpperCase() || "?"}</div>
                        )}
                        <div className="leading-tight">
                          <p className="font-medium text-ink">{u.name}</p>
                          <p className="text-[11px] text-ink-soft">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-ink-soft capitalize">{u.auth_provider}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${u.role === "admin" ? "bg-forest text-paper" : "border border-rule text-ink-soft"}`}>
                        {u.role === "admin" ? "Admin" : "Estudiante"}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {u.plan === "premium" ? (
                        <span className="inline-flex items-center gap-1 text-copper font-medium">
                          <Crown size={11} /> Premium
                        </span>
                      ) : (
                        <span className="text-ink-soft">Gratis</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-ink-soft">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("es-MX") : "—"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        data-testid={`admin-toggle-role-${u.user_id}`}
                        disabled={updating === u.user_id}
                        onClick={() => toggleRole(u)}
                        className="text-[12px] px-3 py-1.5 rounded-full border border-rule hover:bg-paper-2 text-ink-soft hover:text-forest transition-colors disabled:opacity-60"
                      >
                        {updating === u.user_id ? "Actualizando…" : u.role === "admin" ? "Hacer estudiante" : "Hacer admin"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
